/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC, RefObject } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl-next';
import { styled } from '@compiled/react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import isNull from 'lodash/isNull';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { Checkbox } from '@atlaskit/checkbox';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';

import { fireScreenEvent, fireUIClickEvent } from './AnalyticsEvents';
import { ArchiveNestedPagesPermissionWarning } from './ArchiveNestedPagesPermissionWarning';
import { ArchiveFormattedMessageForPage } from './ArchiveFormattedMessageForPage';
import { unarchivePagesRestrictionWarningsI18n } from './UnarchivePagesRestrictionsConstants';
import { BULK_ARCHIVE_PAGE_SELECTION_LIMIT } from './archivingConstants';
import type {
	ArchiveConfirmationMessageQuery as ArchiveConfirmationMessageQuery$data,
	ArchiveConfirmationMessageQueryVariables as ArchiveConfirmationMessageQuery$variables,
} from './__types__/ArchiveConfirmationMessageQuery';
import { PageTreeWithArchivePermissions } from './PageTreeWithArchivePermissions';
import { RestrictionsWarningMessage } from './RestrictionsWarningMessage';
import { archivePagesRestrictionWarningsI18n } from './ArchivePagesRestrictionsConstants';
import { DescendantsNoteApplicationOption } from './__types__/ArchivePagesMutation';

export const INHERITED_RESTRICTIONS_WARNING_TEST_ID =
	'archive-pages-inherited-restrictions-warning';
export const OWN_RESTRICTIONS_WARNING_TEST_ID = 'archive-pages-own-restrictions-warning';

export const i18n = defineMessages({
	archiveNestedPagesDialogCheckboxDescriptionGeneric: {
		id: 'archive-pages.allow-page-nested.archive.dialog.checkbox.description.generic',
		defaultMessage:
			'{count, plural, one {“{title}” has 1 nested item. Check the box if you’d like to archive it, too.} other {“{title}” has {count} nested items. Check the box if you’d like to archive them, too.}}',
		description:
			'The body of the Archive Nested Content item modal dialog. User will be shown the title of the parent content item and has an option to include nested content item with a checkbox.',
	},
	archiveNestedPagesNoPermissionDialogDescription: {
		id: 'archive-pages.allow-page-nested.archive.no-permission.dialog.description.M2',
		defaultMessage:
			'{count, plural, one {“{title}” has 1 nested item.} other {“{title}” has {count} nested items.}}',
		description:
			'The body of the Archive Nested Page modal dialogs when the user does not have permission to archive the nested pages. User will see how many nested pages there are but does not have an option to include the nested pages.',
	},
	archiveNestedPageSelectionLimitReachedWarningMessageV2: {
		id: 'archive-pages.allow-page-nested.archive.selection-limit-reached.dialog.description.v2',
		defaultMessage: `{count, plural, one {“{title}” has 1 nested item.} other {“{title}” has {count} nested items.}}`,
		description:
			'The body of the Archive Nested Page modal dialogs when the user too many nested pages selected to archive.  Message conveys only 500 pages can be moved at a time.',
	},
	unarchiveNestedPagesDialogRadioDescriptionMessage: {
		id: 'archive-pages.allow-page-nested.unarchive.dialog.radio.description.message',
		defaultMessage:
			'{count, plural, one {“{title}” has 1 nested item. Do you want to restore the entire branch?} other {“{title}” has {count} nested items. Do you want to restore the entire branch?}}',
		description:
			'The body of the Restore Nested Content modal dialog. User will see how many nested content items there are and has an option to include nested content items with a radio button.',
	},
	unarchiveNestedPagesNoPermissionDialogDescription: {
		id: 'archive-pages.allow-page-nested.unarchive.no-permission.dialog.description',
		defaultMessage:
			'{count, plural, one {“{title}” has 1 nested item.} other {“{title}” has {count} nested items.}}',
		description:
			'The body of the Restore Nested Page modal dialogs when the user does not have permission to restore the nested pages. User will see how many nested pages there are but does not have an option to include the nested pages.',
	},
	unarchiveNestedPageSelectionLimitReachedWarningMessage: {
		id: 'archive-pages.allow-page-nested.unarchive.selection-limit-reached.dialog.description',
		defaultMessage: `{count, plural, one {“{title}” has 1 nested item. It won’t be restored - only 500 items can move at a time.} other {“{title}” has {count} nested items. They won’t be restored - only 500 items can move at a time.}}`,
		description:
			'The body of the Restore Nested Page modal dialog when the user too many nested pages selected to restore. Message conveys only 500 pages can be moved at a time.',
	},
	archivePageDialogDescription: {
		id: 'archive-pages.page-nested.archive.dialog.description',
		defaultMessage: 'Archiving “{title}” may change who can see the items nested below it.',
		description:
			'The body of the Archive Page modal dialog, warning the user that nested child pages will remain in the page tree after archival.',
	},
	archivePageDialogDescriptionWithRestrictionsWarnings: {
		id: 'archive-pages.page-nested.archive.dialog.restrictions-warnings-enabled.description',
		defaultMessage:
			'“{title}” will be archived but its nested items will remain in the content tree.',
		description:
			'The body of the Archive Page modal dialog when we also have specific restriction change warnings, informing the user that nested child pages will remain in the page tree after archival.',
	},
	unarchivePageWithChildrenDialogBody: {
		id: 'archive-pages.page-nested.unarchive.dialog.body',
		defaultMessage: 'Restoring “{title}” may change who can see the items nested below it.',
		description:
			'The body of the modal dialog that opens when user attempts to unarchive a page that has child (nested) pages, explaining that the children will remain in the archive list after restoring the current page.',
	},
	draftsCantBeArchived: {
		id: 'archive-pages.drafts-archive-message',
		defaultMessage: '*Drafts can’t be archived',
		description:
			'Message that warns user that the archiving functionality does not apply to nested drafts',
	},
	includeNestedPagesArchiveLabel: {
		id: 'archive-pages.archive-nested-message.M2',
		defaultMessage: 'Include nested items',
		description:
			'The label for the checkbox that indicates if the user wants to archive the nested pages',
	},
	includeNestedPagesRestoreLabel: {
		id: 'archive-pages.restore-nested-message',
		defaultMessage:
			'{count, plural, one {Restore {count} nested item.} other {Restore {count} nested items}}',
		description:
			'The label for the checkbox that indicates if the user wants to restore the nested pages',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageTextContainer = styled.div({
	flexGrow: 1,
	marginRight: token('space.300'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageContainer = styled.div({
	display: 'flex',
	flexDirection: 'row',
});

const optionsContainerStyles = xcss({
	marginTop: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageTreeContainer = styled.div({
	borderBottom: `${token('border.width.selected')} solid ${token('color.border')}`,
});

export const INCLUDE_NESTED_PAGES_VALUE: string = 'includeNestedPages';
export const EXCLUDE_NESTED_PAGES_VALUE: string = 'excludeNestedPages';

type ArchiveConfirmationMessageProps = {
	contentId: string;
	// allowNestedPages = true  : Nested pages (branches) can be archived/restored
	// allowNestedPages = false : Single page archive only; nested pages detached and not archived/restored
	allowNestedPages: boolean;
	// includeNestedPages = true  : user opts to include nested pages
	// includeNestedPages = false : user opts to exclude nested pages
	// includeNestedPages = null  : default state + if user is not given the option to include nested pages because of
	//                              missing permissions on all nested pages (archivableDescendantsCount === 0).
	//                              This is effectively 'false' as far as the archive/restore mutation is concerned.
	includeNestedPages: boolean | null;
	setIncludeNestedPages: (boolean) => void;
	descendantsNoteApplicationOption?: DescendantsNoteApplicationOption;
	setDescendantsNoteApplicationOption?: (DescendantsNoteApplicationOption) => void;
	isArchive: boolean;
	analyticsSource: string;
	noteRef?: RefObject<HTMLTextAreaElement>;
	onNoteValidate?: (isValid: boolean) => void;
	isArchiveInProgress?: boolean;
	hasInheritedRestrictions?: boolean;
	hasOwnRestrictions?: boolean;
};

export const ArchiveConfirmationMessageComponent: FC<
	ArchiveConfirmationMessageProps & WrappedComponentProps
> = ({
	contentId,
	allowNestedPages,
	includeNestedPages,
	setIncludeNestedPages,
	setDescendantsNoteApplicationOption = () => {},
	isArchive,
	analyticsSource,
	hasInheritedRestrictions = false,
	hasOwnRestrictions = false,
}: ArchiveConfirmationMessageProps & WrappedComponentProps) => {
	const { data, error } = useQuery<
		ArchiveConfirmationMessageQuery$data,
		ArchiveConfirmationMessageQuery$variables
	>(
		gql`
			query ArchiveConfirmationMessageQuery($contentId: ID!, $showCount: Boolean!) {
				singleContent(id: $contentId) {
					id
					archivableDescendantsCount @include(if: $showCount)
					visibleDescendantsCount @include(if: $showCount)
				}
			}
		`,
		{
			variables: { contentId, showCount: allowNestedPages },
		},
	);
	const countNestedPages = data?.singleContent?.visibleDescendantsCount || 0;
	const isSelectionLimitReached = allowNestedPages
		? countNestedPages + 1 > BULK_ARCHIVE_PAGE_SELECTION_LIMIT
		: false;
	const [showPageTree, setShowPageTree] = useState(true);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		fireScreenEvent({
			createAnalyticsEvent,
			name: analyticsSource,
		});
	}, [createAnalyticsEvent, analyticsSource]);

	const handleShowPageTreeClick = useCallback(
		(shouldIncludeNestedPages: boolean) => {
			fireUIClickEvent({
				createAnalyticsEvent,
				actionSubject: 'link',
				actionSubjectId: 'togglePageTreeLink',
				source: analyticsSource,
				attributes: {
					pageTreeState: shouldIncludeNestedPages ? 'show' : 'hide',
				},
			});
			setShowPageTree(shouldIncludeNestedPages);
		},
		[createAnalyticsEvent, analyticsSource],
	);

	const handleNestedOptionChange = useCallback(
		(shouldIncludeNestedPages: any) => {
			setIncludeNestedPages(() => {
				// TODO: CCAXP-61 update actionSubject potentially?
				if (shouldIncludeNestedPages === false) {
					setDescendantsNoteApplicationOption(DescendantsNoteApplicationOption.NONE);
				}
				fireUIClickEvent({
					createAnalyticsEvent,
					actionSubject: 'radioButton',
					actionSubjectId: 'includeNestedPagesSelection',
					source: analyticsSource,
					attributes: { includeNestedPages: shouldIncludeNestedPages },
				});
				return shouldIncludeNestedPages;
			});
		},
		[
			setIncludeNestedPages,
			createAnalyticsEvent,
			analyticsSource,
			setDescendantsNoteApplicationOption,
		],
	);

	const archivableDescendantsCount = data?.singleContent?.archivableDescendantsCount ?? null;
	const visibleDescendantsCount = data?.singleContent?.visibleDescendantsCount ?? 0;

	useEffect(() => {
		const isIncludeNestedDefault = !allowNestedPages;
		/*
           Select "include nested pages" as default option IFF user has permission to archive/restore at least one descendant.
           Otherwise, we won't display the radio group at all (user effectively has no choice since they are unable
           to archive/restore ANY descendants).
           We wait till we have valid data (here) to set the default option so there is no jumping between selected options.
          */
		if (isIncludeNestedDefault && !isNull(archivableDescendantsCount)) {
			if (archivableDescendantsCount > 0 && !isSelectionLimitReached) {
				setIncludeNestedPages(true);
			}
		}
	}, [
		allowNestedPages,
		archivableDescendantsCount,
		setIncludeNestedPages,
		isSelectionLimitReached,
	]);

	// Using "!data" instead of "loading" with "cache-and-network", as "load" can be true when
	// cached data is available and cause flickering due to reallocating subcomponents
	if (error) {
		return <ErrorDisplay error={error} />;
	} else if (!data && allowNestedPages) {
		return null;
	}

	if (isNull(archivableDescendantsCount)) {
		return null;
	}

	const getI18nMainMessage = () => {
		if (allowNestedPages) {
			if (isSelectionLimitReached) {
				return isArchive
					? i18n.archiveNestedPageSelectionLimitReachedWarningMessageV2
					: i18n.unarchiveNestedPageSelectionLimitReachedWarningMessage;
			} else if (archivableDescendantsCount === 0) {
				// Else no permission to archive or restore any of the descendants
				return isArchive
					? i18n.archiveNestedPagesNoPermissionDialogDescription
					: i18n.unarchiveNestedPagesNoPermissionDialogDescription;
			} else {
				// Else some or all of the descendants can be archived or restored
				return isArchive
					? i18n.archiveNestedPagesDialogCheckboxDescriptionGeneric
					: i18n.unarchiveNestedPagesDialogRadioDescriptionMessage;
			}
		} else {
			// else allowNestedPages === false

			return isArchive
				? i18n.archivePageDialogDescriptionWithRestrictionsWarnings
				: i18n.unarchivePageWithChildrenDialogBody;
		}
	};

	const i18nNestedLabel = isArchive
		? i18n.includeNestedPagesArchiveLabel
		: i18n.includeNestedPagesRestoreLabel;

	// Note that items can have both inherited and own restrictions at the same time.  This is
	// because restrictions are additive: own restrictions are layered on top of any restrictions
	// already inherited by that item. That means moving or changing what an item inherits from will
	// always change that item's restrictions and that of any item underneath it. (Also note - and
	// this only applies to Bulk Archiving - that once a page has own or inherited restrictions then
	// its all its children will have inherited restrictions; there's no way to get back to the "no
	// restrictions" state further down that branch.)
	//
	// The rules for showing the restriction warnings are as follows:
	//  1. If an item has inherited restrictions and it or any of its children are archived, then
	//     warn about the items losing their restrictions in the archive
	//  2. If an item with own restrictions is archived and of any of its children are not included,
	//     then warn about moving the remaining items up in the tree
	//  There are also additional rules that only apply to Bulk Archiving - one that applies only
	//  when the selected item has only isn't archived and one that only applies to excluded items on
	//  Bulk Archive - that don't apply here. See comments in BulkArchivePagesConfirmationDialog.tsx
	//  for those rules.)
	//
	// Note that showing both restriction warnings is possible: when an item has both inherited and
	// own restrictions and some of the children aren't included - either because the user chose not
	// to archive the children or some children don't have permission. In those cases, the children
	// that will be archived need the "losing inheritence in archive" warning, while the children
	// that aren't archived will lose the parent's own restrictions and thus need the own
	// restrictions "moving up the page tree" warning too.

	const getInheritedRestrictionsWarningMessage = () => {
		// If the selected item has inherited restrictions, show a warning message about the selected
		// item and its children losing their restrictions if archived or just the selected item
		// losing it restrictions if no children are included. (Note that selected items that
		// don't have any children don't end up in this component at all)
		if (hasInheritedRestrictions) {
			// Treat the operation as not including nested pages if the user doesn't have permission to
			// archive any of the nested pages. Also, if the selection limit has been reached, we only
			// allow the user to archive the selected item and not any of the children.
			return includeNestedPages && archivableDescendantsCount > 0 && !isSelectionLimitReached ? (
				<RestrictionsWarningMessage
					dataTestid={INHERITED_RESTRICTIONS_WARNING_TEST_ID}
					title={archivePagesRestrictionWarningsI18n.inheritedRestrictionsPageAndChildrenTitle}
					message={archivePagesRestrictionWarningsI18n.inheritedRestrictionsPageAndChildrenMessage}
				/>
			) : (
				<RestrictionsWarningMessage
					// It's okay for this intance to share the same data-testid as the previous on since
					// they should never be shown at the same time
					dataTestid={INHERITED_RESTRICTIONS_WARNING_TEST_ID}
					title={archivePagesRestrictionWarningsI18n.inheritedRestrictionsPageOnlyTitle}
					message={archivePagesRestrictionWarningsI18n.inheritedRestrictionsPageOnlyMessage}
				/>
			);
		}

		// Else the selected item doesn't have inherited restrictions, so we don't need a warning message
		return null;
	};

	const getOwnRestrictionsWarningMessage = () => {
		// If the selected item has its own restrictions and the some or all of the children aren't
		// aren't being archived, show the warning message about the children changing their
		// restrictions because they are moving up in the page tree.
		// (Note that if the selected item has its only own restrictions, then archiving it with all
		//  of its children will keep all their restrictions intact, so no warning is needed in that
		//  case.)
		if (hasOwnRestrictions) {
			// If any of the nested items are not being archived, show a message that they will lose
			// some of all of their restrictions. Note that when the selection limit has been reached,
			// we only allow the user to archive the selected item and not any of the children. Also
			// remember, we have to have some children here because selected items that don't have any
			// children won't end up in this component at all.
			if (
				!includeNestedPages ||
				archivableDescendantsCount < visibleDescendantsCount ||
				isSelectionLimitReached
			) {
				return (
					<RestrictionsWarningMessage
						dataTestid={OWN_RESTRICTIONS_WARNING_TEST_ID}
						title={archivePagesRestrictionWarningsI18n.ownRestrictionsChildrenTitle}
						message={archivePagesRestrictionWarningsI18n.ownRestrictionsChildrenMessage}
					/>
				);
			}
		}

		// There are no own restriction cases that need a warning, so return nothing
		return null;
	};

	const getRestoreInheritedRestrictionsWarningMessage = () => {
		// If the selected item has inherited restrictions, show a warning message about the selected
		// item and its children losing their restrictions if restored or just the selected item
		// losing it restrictions if no children are included. (Note that selected items that
		// don't have any children don't end up in this component at all)
		if (hasInheritedRestrictions) {
			// Treat the operation as not including nested pages if the user doesn't have permission to
			// archive any of the nested pages. Also, if the selection limit has been reached, we only
			// allow the user to archive the selected item and not any of the children.
			return includeNestedPages && archivableDescendantsCount > 0 && !isSelectionLimitReached ? (
				<RestrictionsWarningMessage
					title={
						unarchivePagesRestrictionWarningsI18n.unarchiveInheritedRestrictionsWithChildrenWarningTitle
					}
					message={
						unarchivePagesRestrictionWarningsI18n.unarchiveInheritedRestrictionsWithChildrenWarningMessage
					}
				/>
			) : (
				<RestrictionsWarningMessage
					title={
						unarchivePagesRestrictionWarningsI18n.unarchiveInheritedRestrictionsNoChildrenWarningTitle
					}
					message={
						unarchivePagesRestrictionWarningsI18n.unarchiveInheritedRestrictionsNoChildrenWarningMessage
					}
				/>
			);
		}

		// Else the selected item doesn't have inherited restrictions, so we don't need a warning message
		return null;
	};

	const getRestoreOwnRestrictionsWarningMessage = () => {
		// If the selected item has its own restrictions and the some or all of the children aren't
		// aren't being restored, show the warning message about the children changing their
		// restrictions because they are moving up in the page tree.
		// (Note that if the selected item has its only own restrictions, then restoring it with all
		//  of its children will keep all their restrictions intact, so no warning is needed in that
		//  case.)
		if (hasOwnRestrictions) {
			// If any of the nested items are not being restored, show a message that they will lose
			// some of all of their restrictions. Note that when the selection limit has been reached,
			// we only allow the user to restore the selected item and not any of the children. Also
			// remember, we have to have some children here because selected items that don't have any
			// children won't end up in this component at all.
			if (
				!includeNestedPages ||
				archivableDescendantsCount < visibleDescendantsCount ||
				isSelectionLimitReached
			) {
				return (
					<RestrictionsWarningMessage
						title={
							unarchivePagesRestrictionWarningsI18n.unarchiveOwnRestrictionsNoChildrenWarningTitle
						}
						message={
							unarchivePagesRestrictionWarningsI18n.unarchiveOwnRestrictionsNoChildrenWarningMessage
						}
					/>
				);
			}
		}

		// There are no own restriction cases that need a warning, so return nothing
		return null;
	};

	return (
		<ContentContainer>
			<MessageContainer data-testid="archive-confirmation-msg-container">
				<MessageTextContainer>
					<ArchiveFormattedMessageForPage
						contentId={contentId}
						i18nMessage={getI18nMainMessage()}
						allowNestedPages={allowNestedPages}
						useArchivableCount={false}
						shouldTruncate
					/>
				</MessageTextContainer>
			</MessageContainer>
			{allowNestedPages
				? !isSelectionLimitReached && (
						<>
							{archivableDescendantsCount === 0 ? null : (
								// show checkbox if there is at least 1 nested page
								<Box xcss={optionsContainerStyles}>
									<Checkbox
										label={
											<FormattedMessage
												{...i18nNestedLabel}
												values={{
													count: visibleDescendantsCount,
												}}
											/>
										}
										onChange={() => {
											const shouldIncludeNestedPages = !Boolean(includeNestedPages);
											handleNestedOptionChange(shouldIncludeNestedPages);
											handleShowPageTreeClick(shouldIncludeNestedPages);
										}}
										isChecked={Boolean(includeNestedPages)}
										name="checkbox-include-nested-pages"
										testId="cb-include-nested-pages"
									/>
								</Box>
							)}
						</>
					)
				: null}
			{allowNestedPages &&
				// Show permission warning if the selection limit has been reached.
				(isSelectionLimitReached ||
					// Show nested pages warning if user opted to include descendants (includeNestedPages = true)
					// OR if we didn't give user the option to include/exclude (includeNestedPages = null).
					// The nested pages permission warning is irrelevant if user opts to exclude nested pages.
					includeNestedPages !== false) && (
					<ArchiveNestedPagesPermissionWarning
						isArchive={isArchive}
						contentId={contentId}
						isSelectionLimitReached={isSelectionLimitReached}
					/>
				)}
			{isArchive && getInheritedRestrictionsWarningMessage()}
			{isArchive && getOwnRestrictionsWarningMessage()}
			{!isArchive && getRestoreInheritedRestrictionsWarningMessage()}
			{!isArchive && getRestoreOwnRestrictionsWarningMessage()}
			{showPageTree &&
			// This check ensures that the page tree is not shown when the selection limit has been reached
			!isSelectionLimitReached ? (
				<PageTreeContainer data-testid="table-tree-container">
					<PageTreeWithArchivePermissions
						contentId={contentId}
						analyticsSource={analyticsSource}
						isArchive={isArchive}
					/>
				</PageTreeContainer>
			) : null}
		</ContentContainer>
	);
};

export const ArchiveConfirmationMessage = injectIntl(ArchiveConfirmationMessageComponent);
